import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../config/api';
import { toast } from 'react-toastify';
import { FaUser, FaLock, FaCamera, FaSave, FaEye, FaEyeSlash, FaUsers, FaPlus, FaTrash, FaEnvelope, FaCreditCard, FaCalendarAlt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './Account.css';

const Account = () => {
  const { currentUser, updateProfile, uploadProfilePhoto } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [profileData, setProfileData] = useState({
    displayName: currentUser?.displayName || '',
    publicUsername: currentUser?.publicUsername || '',
    phone: currentUser?.phone || '',
    jobRole: currentUser?.jobRole || ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [loading, setLoading] = useState({ profile: false, password: false, photo: false, team: false });
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');

  const isCorporate = currentUser?.role === 'subscriber';
  const isAccountCreator = currentUser?.isAccountCreator;

  useEffect(() => {
    if (isAccountCreator) {
      loadTeamMembers();
    }
  }, [isAccountCreator]);

  const loadTeamMembers = async () => {
    try {
      const members = await api.getTeamMembers();
      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to load system administrators:', error);
    }
  };

  const handleAddTeamMember = async () => {
    if (!newMemberEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    const isAuthorityType = currentUser?.corporate_type === 'authority';
    if (isAuthorityType && teamMembers.length >= 5) {
      toast.error('Maximum 5 system administrators allowed');
      return;
    }
    try {
      setLoading({ ...loading, team: true });
      await api.addTeamMember(newMemberEmail.trim());
      toast.success('System administrator invited successfully');
      setNewMemberEmail('');
      loadTeamMembers();
    } catch (error) {
      toast.error(error.message || 'Failed to add team member');
    } finally {
      setLoading({ ...loading, team: false });
    }
  };

  const handleRemoveTeamMember = async (id) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      await api.removeTeamMember(id);
      toast.success('Team member removed');
      loadTeamMembers();
    } catch (error) {
      toast.error(error.message || 'Failed to remove team member');
    }
  };

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileData.displayName.trim()) {
      toast.error('Full name is required');
      return;
    }
    try {
      setLoading({ ...loading, profile: true });
      await updateProfile(profileData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading({ ...loading, profile: false });
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    try {
      setLoading({ ...loading, password: true });
      await api.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setLoading({ ...loading, password: false });
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    try {
      setLoading({ ...loading, photo: true });
      await uploadProfilePhoto(file);
      toast.success('Profile photo updated');
    } catch (error) {
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setLoading({ ...loading, photo: false });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  if (!currentUser) {
    return (
      <div className="account-page">
        <div className="account-container">
          <p>Please log in to access your account settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-container">
        <h1 className="account-title">Account Settings</h1>
        
        <div className="account-sections">
          <section className="account-section photo-section">
            <div className="photo-container" onClick={handlePhotoClick}>
              {currentUser.profilePhoto ? (
                <img src={currentUser.profilePhoto} alt="Profile" className="profile-photo" />
              ) : (
                <div className="photo-placeholder">
                  <FaUser />
                </div>
              )}
              <div className="photo-overlay">
                <FaCamera />
                <span>{loading.photo ? 'Uploading...' : 'Change Photo'}</span>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
            <p className="photo-hint">Click to upload a profile photo</p>
          </section>

          <section className="account-section">
            <h2><FaUser /> Profile Information</h2>
            <form onSubmit={handleProfileSubmit}>
              <div className="form-group">
                <label htmlFor="displayName">Full Name</label>
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  value={profileData.displayName}
                  onChange={handleProfileChange}
                  placeholder="Your full name"
                />
                <small className="form-help">
                  This is visible to corporate subscribers for verification
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="publicUsername">Public Username <span className="optional-label">(Optional)</span></label>
                <input
                  type="text"
                  id="publicUsername"
                  name="publicUsername"
                  value={profileData.publicUsername}
                  onChange={handleProfileChange}
                  placeholder="Anonymous display name"
                />
                <small className="form-help">
                  This will be shown on the Personal Dashboard instead of your real name
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number <span className="optional-label">(Optional)</span></label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                  placeholder="Your phone number"
                />
              </div>

              {isCorporate && (
                <div className="form-group">
                  <label htmlFor="jobRole">Position / Job Title <span className="optional-label">(Optional)</span></label>
                  <input
                    type="text"
                    id="jobRole"
                    name="jobRole"
                    value={profileData.jobRole}
                    onChange={handleProfileChange}
                    placeholder="Your position or job title"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Email Address</label>
                <input type="email" value={currentUser.email} disabled className="disabled-input" />
                <small className="form-help">Email cannot be changed</small>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading.profile}>
                <FaSave /> {loading.profile ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </section>

          <section className="account-section">
            <h2><FaLock /> Change Password</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group password-field">
                <label htmlFor="currentPassword">Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    id="currentPassword"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility('current')}
                  >
                    {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group password-field">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="At least 6 characters"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility('new')}
                  >
                    {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group password-field">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Re-enter new password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility('confirm')}
                  >
                    {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <button type="submit" className="btn btn-secondary" disabled={loading.password}>
                <FaLock /> {loading.password ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </section>

          {isAccountCreator && (
            <section className="account-section team-section">
              <h2><FaUsers /> System Administrators</h2>
              <p className="form-help" style={{ marginBottom: '20px' }}>
                Manage system administrators who can access your corporate dashboard.
                {currentUser?.corporate_type === 'authority' 
                  ? ' Maximum 5 administrators.' 
                  : ' Unlimited administrators.'}
              </p>

              <div className="add-member-form">
                <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                  <input
                    type="email"
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    placeholder="Enter email address"
                    disabled={loading.team || (currentUser?.corporate_type === 'authority' && teamMembers.length >= 5)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddTeamMember}
                  disabled={loading.team || (currentUser?.corporate_type === 'authority' && teamMembers.length >= 5)}
                >
                  <FaPlus /> {loading.team ? 'Adding...' : 'Add Administrator'}
                </button>
              </div>

              {teamMembers.length === 0 ? (
                <div className="no-members">
                  <p>No system administrators added yet.</p>
                </div>
              ) : (
                <div className="team-members-list">
                  {teamMembers.map((member) => (
                    <div key={member.id} className="team-member-item">
                      <div className="member-info">
                        <FaEnvelope className="member-icon" />
                        <div>
                          <strong>{member.email}</strong>
                          <span className={`member-status status-${member.status}`}>
                            {member.status === 'invited' ? 'Pending Invitation' : 'Active'}
                          </span>
                          {member.full_name && <small>{member.full_name}</small>}
                          {member.job_title && <small> - {member.job_title}</small>}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveTeamMember(member.id)}
                        title="Remove administrator"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <small className="form-help" style={{ marginTop: '15px', display: 'block' }}>
                {currentUser?.corporate_type === 'authority' 
                  ? `System administrators (${teamMembers.length}/5)` 
                  : `System administrators (${teamMembers.length})`} will receive an email with login credentials when invited.
              </small>
            </section>
          )}

          {isCorporate && (
            <section className="account-section subscription-section">
              <h2><FaCreditCard /> Subscription Management</h2>
              <div className="subscription-info">
                <div className="subscription-status-card">
                  <div className="status-header">
                    {currentUser?.subscription?.active ? (
                      <>
                        <FaCheckCircle className="status-icon active" />
                        <span className="status-text active">Active Subscription</span>
                      </>
                    ) : (
                      <>
                        <FaExclamationTriangle className="status-icon expired" />
                        <span className="status-text expired">Subscription Expired</span>
                      </>
                    )}
                  </div>
                  
                  <div className="subscription-details">
                    <div className="detail-row">
                      <span className="detail-label">Plan Type:</span>
                      <span className="detail-value">
                        {currentUser?.subscription?.type === 'authority' ? 'Public Authority' :
                         currentUser?.subscription?.type === 'private_company' ? 'Waste Management Company' :
                         currentUser?.subscription?.type === 'recycling' ? 'Recycling Business' : 'Unknown'}
                      </span>
                    </div>
                    {currentUser?.subscription?.startDate && (
                      <div className="detail-row">
                        <span className="detail-label"><FaCalendarAlt /> Start Date:</span>
                        <span className="detail-value">
                          {new Date(currentUser.subscription.startDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {currentUser?.subscription?.coupon && (
                      <div className="detail-row">
                        <span className="detail-label">Applied Coupon:</span>
                        <span className="detail-value coupon-badge">
                          {currentUser.subscription.coupon.code} ({currentUser.subscription.coupon.discountPercent}% off)
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="subscription-actions">
                    <button 
                      className="btn btn-primary btn-renew"
                      onClick={() => navigate('/subscribe')}
                    >
                      <FaCreditCard /> {currentUser?.subscription?.active ? 'Upgrade / Renew Plan' : 'Resubscribe Now'}
                    </button>
                    <p className="renew-hint">
                      {currentUser?.subscription?.active 
                        ? 'Upgrade your plan or add more time to your subscription'
                        : 'Renew your subscription to continue accessing corporate features'}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;
